import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useToasts } from "../../contexts/toasts";
import { useAuth } from "../../contexts/auth";
import { Roles } from "../../enums/Roles";
import { Programmazione } from "../../types/Programmazione";
import type { Option } from "../../types/generics";
import { stripLegacyExerciseFields } from "../../utils/splitExerciseStripNormalizer";
import { Programmazioni } from "../../api/Programmazioni";
import { Entities } from "../../api/Entities";
import { useEntityOptions, type ProgEditorOptions } from "../../hooks/useEntityOptions";
import { useProgrammazioneMutations } from "../../hooks/useProgrammazioneMutations";
import { useAssignableUsers } from "../../hooks/useAssignableUsers";
import { Container } from "react-bootstrap";
import { Helmet } from "react-helmet-async";
import { Alert, Breadcrumb, Button, Card, Col, Flex, Row, Select, Skeleton, Tag, theme } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faEdit,
  faCalendarAlt,
  faLayerGroup,
  faDumbbell,
  faUser,
  faCopy,
  faGlobe,
} from "@fortawesome/free-solid-svg-icons";
import { useClipboard } from "../../contexts/clipboard";
import Title from "antd/es/typography/Title";
import dayjs from "dayjs";
import "dayjs/locale/it";
import SplitListView from "../../Components/View/SplitListView";
import "./ProgrammazioneDetail.css";

dayjs.locale("it");

export default function ProgrammazioneDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToasts();
  const { loggedUser } = useAuth();
  // PROG-WEEK-PARAMS/FE-4 — page-level lift mirroring EditProgrammazione
  // (FE-1 Q5). The Detail view mounts ExerciseProgressionEditor in
  // trainer-view mode in every ExerciseCardView; lifting here avoids one
  // useEntityOptions hook per card.
  // Only trainers/superadmins ever edit a programmazione. Athletes are
  // read-only viewers — and crucially the editor-support endpoints (entity
  // catalog, exercises, muscle groups, assignable users) are all isAdmin-gated
  // on the BE, so calling them as an athlete returns 401 and bounces to login.
  // Gate every admin-only fetch on this flag; the whole page renders read-only.
  const isEditor =
    loggedUser?.role === Roles.Superadmin || loggedUser?.role === Roles.Trainer;

  const {
    options: entityOpts,
    loadRepSchemesByType,
    loadEffortsByType,
  } = useEntityOptions(isEditor);
  const progEditorOptions = useMemo<ProgEditorOptions>(
    () => ({
      repSchemeTypeOptions: entityOpts.repSchemeTypes,
      effortTypeOptions: entityOpts.effortTypes,
      tutOptions: entityOpts.tuts,
      tecnicaOptions: entityOpts.tecniche,
      tecnicheRaw: entityOpts.tecnicheRaw,
      recOptions: entityOpts.recs,
      loadRepSchemesByType,
      loadEffortsByType,
    }),
    [entityOpts, loadRepSchemesByType, loadEffortsByType],
  );
  const { users: assignableUsers, loading: loadingUsers } = useAssignableUsers(isEditor);
  const { copySplits } = useClipboard();
  const { token } = theme.useToken();

  const [programmazione, setProgrammazione] = useState<Programmazione>();
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);
  // PROG-DETAIL identity flow — catalog exercises + muscle groups for the
  // add/edit ExerciseIdentityModal, threaded down to the exercise cards.
  // exercisesOptions[*].value is the full Exercise (so the modal can read
  // .muscleGroupId/.id), mirroring the create/edit form's option shape.
  const [exercisesOptions, setExercisesOptions] = useState<Option[]>([]);
  const [muscleGroupOptions, setMuscleGroupOptions] = useState<Option[]>([]);

  const mutations = useProgrammazioneMutations({
    programmazione,
    setProgrammazione,
    id,
  });

  useEffect(() => {
    if (!id) {
      navigate("/programmazioni");
      return;
    }

    // Athletes are read-only: fetch ONLY the programmazione (single/:id is not
    // admin-gated). The catalog fetches (exercises, muscle groups) are
    // isAdmin-gated → 401 → login bounce, so they're skipped. The exercise
    // names/images the athlete sees come from the data persisted on the
    // programmazione itself.
    Promise.all([
      Programmazioni.getProgrammazione(id),
      isEditor ? Entities.getExercises() : Promise.resolve(null),
      isEditor ? Entities.getMuscleGroups() : Promise.resolve(null),
    ])
      .then(([progRes, exRes, mgRes]) => {
        const prog = progRes.data.programmazione;
        const exercises = exRes?.data.list ?? [];

        setExercisesOptions(
          exercises.map((ex) => ({ label: ex.name, value: ex }))
        );
        setMuscleGroupOptions(
          (mgRes?.data.list ?? []).map((mg) => ({ label: mg.muscleGroup, value: mg.id }))
        );

        const exerciseMap = new Map(
          exercises.map((ex) => [ex.id, ex])
        );
        prog.splits?.forEach((split) => {
          split.exercises?.forEach((exercise) => {
            if (exercise.type === "catalog" && exercise.exerciseId) {
              const catalogEx = exerciseMap.get(exercise.exerciseId);
              if (catalogEx?.signedUrl) {
                exercise.imageUrl = catalogEx.signedUrl;
              }
              // PROG-DETAIL/IMPL K4 — patch customName from the catalog if
              // the saved row didn't persist it. Without this the View falls
              // back to the generic "Esercizio da catalogo" placeholder.
              if (catalogEx?.name && !exercise.customName?.trim()) {
                exercise.customName = catalogEx.name;
              }
            }
          });
        });

        const lean = stripLegacyExerciseFields(prog);
        setProgrammazione(lean);
        setLoading(false);
      })
      .catch(() => {
        showToast(
          "programmazione-error",
          "Errore nel reperimento della Programmazione",
          "",
          "danger"
        );
        setError(true);
        setLoading(false);
      });
  }, [id, navigate, showToast, isEditor]);

  const canEdit = useCallback(() => {
    if (!loggedUser || !programmazione) return false;
    if (loggedUser.role === Roles.Superadmin) return true;
    if (
      loggedUser.role === Roles.Trainer &&
      programmazione.parentId === loggedUser.id
    )
      return true;
    return false;
  }, [loggedUser, programmazione]);

  const isTargetAthlete =
    loggedUser?.id === programmazione?.userId;

  const breadcrumbItems = [
    {
      title: (
        <a href="/programmazioni" style={{ textDecoration: "none" }}>
          <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
          Programmazioni
        </a>
      ),
    },
    { title: programmazione?.name || "Dettaglio" },
  ];

  if (loading) {
    // PROG-DETAIL/IMPL Q2 — Skeleton mock of the Detail layout (header,
    // info-card grid, and two split-card placeholders) instead of a bare
    // spinner, so the loading state visually approximates the rendered
    // page.
    return (
      <Container fluid="lg" className="my-3">
        <Breadcrumb items={breadcrumbItems} className="mb-3" />
        <Skeleton active title paragraph={{ rows: 1, width: ["40%"] }} />
        <Card className="my-3">
          <Skeleton active title={false} paragraph={{ rows: 2 }} />
        </Card>
        <Skeleton active title paragraph={{ rows: 4 }} className="mt-4" />
        <Skeleton active title paragraph={{ rows: 4 }} className="mt-4" />
      </Container>
    );
  }

  if (error || !programmazione) {
    return (
      <Container fluid="lg" className="my-3">
        <Breadcrumb items={breadcrumbItems} className="mb-3" />
        <Alert
          type="error"
          showIcon
          message="Si è verificato un errore nel reperimento dei dati della Programmazione. Riprova più tardi."
        />
      </Container>
    );
  }

  const scadenzaFormatted = programmazione.scadenza
    ? dayjs(programmazione.scadenza).format("dddd D MMMM YYYY")
    : "Nessuna scadenza";

  const totalExercises = programmazione.splits?.reduce(
    (acc, split) => acc + (split.exercises?.length || 0),
    0
  );

  const userCanEdit = canEdit();
  const assignedUserLabel =
    programmazione.userId != null
      ? assignableUsers.find((u) => u.value === programmazione.userId)?.label
      : undefined;

  const statIconStyle: React.CSSProperties = {
    color: token.colorPrimary,
    width: 18,
  };

  return (
    <Container fluid="lg" className="my-3">
      <Helmet>
        <title>{programmazione.name} - Dettaglio Programmazione</title>
      </Helmet>

      <Breadcrumb items={breadcrumbItems} className="mb-3" />

      {/* PROG-DETAIL/IMPL G1 + G2 — sticky header mirroring EditProgrammazione,
          AntD Buttons for Copia/Modifica. G9 — Title level=2 for visual balance. */}
      <div
        style={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          background: token.colorBgContainer,
          padding: "12px 16px",
          marginInline: -16,
          borderBottom: `1px solid ${token.colorBorderSecondary}`,
          boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
          marginBottom: 16,
        }}
      >
        <Flex
          justify="space-between"
          align="center"
          className="flex-column flex-md-row"
          gap={12}
        >
          <div className="text-center text-md-start">
            <Flex align="center" gap="middle" wrap="wrap">
              <Title level={2} className="mb-0">
                {programmazione.name}
              </Title>
              {programmazione.isPublic && (
                <Tag
                  color="blue"
                  icon={<FontAwesomeIcon icon={faGlobe} className="me-1" />}
                >
                  Pubblica
                </Tag>
              )}
            </Flex>
          </div>
          <Flex gap={8} className="mt-3 mt-md-0">
            {programmazione.splits?.length > 0 && (
              <Button
                type="default"
                className="cm-btn cm-btn--ghost"
                onClick={() => {
                  copySplits(programmazione.splits);
                  showToast("copy-programmazione", "Programmazione copiata", "", "success");
                }}
                icon={<FontAwesomeIcon icon={faCopy} />}
              >
                <span className="d-none d-sm-inline">Copia</span>
              </Button>
            )}
            {userCanEdit && (
              <Button
                type="default"
                className="cm-btn cm-btn--primary"
                onClick={() => navigate(`/programmazioni/${id}/edit`)}
                icon={<FontAwesomeIcon icon={faEdit} />}
              >
                <span className="d-none d-sm-inline">Modifica</span>
              </Button>
            )}
          </Flex>
        </Flex>
      </div>

      {/* PROG-DETAIL/IMPL G3 — Info card on AntD grid. G8 — assigned user
          surfaced for non-editors as a read-only stat cell, editable Select
          only for editors. G4 — icons in primary color, default size. */}
      <Card className="bg-card rounded-lg shadow-sm border mb-4">
        <Title level={3} className="mb-4">
          Informazioni Generali
        </Title>
        <Row gutter={[24, 16]} align="middle">
          <Col xs={24} sm={12} md={12} lg={6}>
            <Flex align="center" gap="small">
              <FontAwesomeIcon icon={faLayerGroup} style={statIconStyle} />
              <div>
                <div className="text-muted small">Split</div>
                <strong>{programmazione.nSplits} Split</strong>
              </div>
            </Flex>
          </Col>
          <Col xs={24} sm={12} md={12} lg={6}>
            <Flex align="center" gap="small">
              <FontAwesomeIcon icon={faDumbbell} style={statIconStyle} />
              <div>
                <div className="text-muted small">Esercizi Totali</div>
                <strong>
                  {totalExercises} Eserciz
                  {totalExercises !== 1 ? "i" : "io"}
                </strong>
              </div>
            </Flex>
          </Col>
          <Col xs={24} sm={12} md={12} lg={6}>
            <Flex align="center" gap="small">
              <FontAwesomeIcon icon={faCalendarAlt} style={statIconStyle} />
              <div>
                <div className="text-muted small">Scadenza</div>
                <strong style={{ textTransform: "capitalize" }}>
                  {scadenzaFormatted}
                </strong>
              </div>
            </Flex>
          </Col>
          <Col xs={24} sm={12} md={12} lg={6}>
            <Flex align="center" gap="small" style={{ minWidth: 0 }}>
              <FontAwesomeIcon icon={faUser} style={statIconStyle} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="text-muted small mb-1">Assegnato a</div>
                {userCanEdit ? (
                  <Select
                    style={{ width: "100%" }}
                    placeholder="Seleziona un utente..."
                    showSearch
                    allowClear
                    loading={loadingUsers}
                    value={programmazione.userId ?? undefined}
                    onChange={(value) => mutations.updateUserId(value ?? null)}
                    options={assignableUsers}
                    filterOption={(input, option) =>
                      (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                    }
                  />
                ) : (
                  <strong>{assignedUserLabel || "—"}</strong>
                )}
              </div>
            </Flex>
          </Col>
        </Row>
      </Card>

      <SplitListView
        splits={programmazione.splits || []}
        canEdit={userCanEdit}
        isTargetAthlete={isTargetAthlete}
        entityOpts={entityOpts}
        progEditorOptions={progEditorOptions}
        mutations={mutations}
        exercisesOptions={exercisesOptions}
        muscleGroupOptions={muscleGroupOptions}
      />
    </Container>
  );
}
